import { RECENT_PERIODS } from "../constants/date";
import { getDateWithOffset, normalizeDate, recentPeriodToNoOfDays } from "./date";
import type { DateRange, RecentPeriod } from "../types/date";

export type DateRangeSearchParams = {
  startDate?: string;
  endDate?: string;
  quickSelect?: RecentPeriod;
};

export const isIsoDateString = (value: unknown): value is string =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

export const isRecentPeriod = (value: unknown): value is RecentPeriod =>
  (RECENT_PERIODS as readonly string[]).includes(value as string);

export const toIsoDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const parseDateRangeSearchParams = (search: Record<string, unknown>): DateRangeSearchParams => ({
  startDate: isIsoDateString(search.startDate) ? search.startDate : undefined,
  endDate: isIsoDateString(search.endDate) ? search.endDate : undefined,
  quickSelect: isRecentPeriod(search.quickSelect) ? search.quickSelect : undefined,
});

function computeDateRangeForPeriod(period: RecentPeriod): DateRange {
  const noOfDays = recentPeriodToNoOfDays(period);
  const endDate = normalizeDate(getDateWithOffset(new Date(), -1));
  const startDate = normalizeDate(getDateWithOffset(endDate, -(noOfDays - 1)));
  return { startDate, endDate };
}

export const resolveDateRange = ({ startDate, endDate, quickSelect }: DateRangeSearchParams): DateRange => {
  if (quickSelect) {
    return computeDateRangeForPeriod(quickSelect);
  }
  if (startDate && endDate) {
    const [sy, sm, sd] = startDate.split("-").map(Number);
    const [ey, em, ed] = endDate.split("-").map(Number);
    const resolvedStart = normalizeDate(new Date(sy, sm - 1, sd));
    const resolvedEnd = normalizeDate(new Date(ey, em - 1, ed));
    if (toIsoDateString(resolvedStart) !== startDate || toIsoDateString(resolvedEnd) !== endDate) {
      return computeDateRangeForPeriod("LAST_14");
    }
    return { startDate: resolvedStart, endDate: resolvedEnd };
  }
  return computeDateRangeForPeriod("LAST_14");
};
